import { type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'

import { cn } from '../cn'
import { ToggleSwitch } from '../forms/Toggle'

// Horarios dimension (design/components/bot-config-schedule.html). Defines when
// the bot operates. WeeklyScheduleEditor sets hours per day (close must be after
// open); ExceptionsList overrides specific dates; OutOfHoursMessageEditor is the
// honest closed-reply (must keep an emergency exit); ProactiveWindowPicker bounds
// when the bot may start a conversation — always inside WhatsApp's free 24h
// window. "Closed" is shown as text, never the toggle state alone; proactive day
// buttons carry the full day name (not just an ambiguous initial).

// Days are Monday-first; `day` indexes this order (0 = Monday).
const DAY_KEYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const

export interface DaySchedule {
  day: number
  open: boolean
  /** "HH:MM" */
  from?: string
  /** "HH:MM" */
  to?: string
}

export interface WeeklyScheduleEditorProps {
  week: DaySchedule[]
  onChange: (week: DaySchedule[]) => void
  className?: string
}

/** Per-day open/closed toggles + HH:MM inputs for Horarios (Monday-first).
 *  Closed shows as text, never the toggle state alone; a `to ≤ from` range
 *  flags an inline role=alert error yet still reaches onChange. */
export function WeeklyScheduleEditor({ week, onChange, className }: WeeklyScheduleEditorProps) {
  const { t } = useTranslation('components')

  function update(index: number, patch: Partial<DaySchedule>) {
    onChange(week.map((d, i) => (i === index ? { ...d, ...patch } : d)))
  }

  return (
    <div className={cn('flex flex-col', className)}>
      {week.map((d, i) => {
        const dayKey = DAY_KEYS[d.day] ?? DAY_KEYS[i]
        const dayName = t(`schedule.days.${dayKey}.name`)
        const invalid = d.open && d.from && d.to ? d.to <= d.from : false
        return (
          <div key={i} className="flex flex-col gap-1 border-b border-line py-2.5 last:border-b-0">
            <div className="flex items-center gap-3">
              <span className="w-24 flex-none text-[12.5px] font-semibold text-text">
                {dayName}
              </span>
              {d.open ? (
                <span className="flex items-center gap-2">
                  <input
                    type="time"
                    value={d.from ?? ''}
                    aria-label={`${dayName} · ${t('schedule.from')}`}
                    onChange={(e) => update(i, { from: e.target.value })}
                    className="rounded-lg border border-input-line bg-surface px-2 py-1 font-mono text-[12px] text-text outline-none focus-visible:border-brand-blue"
                  />
                  <span aria-hidden className="text-text-3">
                    –
                  </span>
                  <input
                    type="time"
                    value={d.to ?? ''}
                    aria-label={`${dayName} · ${t('schedule.to')}`}
                    onChange={(e) => update(i, { to: e.target.value })}
                    className="rounded-lg border border-input-line bg-surface px-2 py-1 font-mono text-[12px] text-text outline-none focus-visible:border-brand-blue"
                  />
                </span>
              ) : (
                <span className="text-[12px] font-medium text-text-3">{t('schedule.closed')}</span>
              )}
              <ToggleSwitch
                checked={d.open}
                onChange={(open) => update(i, { open })}
                label={t('schedule.openAria', { day: dayName })}
                hideLabel
                size="sm"
                className="ml-auto"
              />
            </div>
            {invalid && (
              <span role="alert" className="ml-24 pl-3 text-[11px] text-sens">
                {t('schedule.invalidRange')}
              </span>
            )}
          </div>
        )
      })}
    </div>
  )
}

function XIcon() {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2.2}
      strokeLinecap="round"
      aria-hidden
    >
      <path d="M18 6 6 18M6 6l12 12" />
    </svg>
  )
}

export interface ScheduleException {
  /** Display date, e.g. "16 sep". */
  date: string
  label: string
  closed?: boolean
}

export interface ExceptionsListProps {
  exceptions: ScheduleException[]
  onAdd?: () => void
  onRemove: (index: number) => void
  className?: string
}

/** Date-specific overrides of the weekly Horarios: each row shows date, label,
 *  and an open/closed tag (`closed` omitted counts as closed). The add button
 *  renders only when `onAdd` is provided. */
export function ExceptionsList({ exceptions, onAdd, onRemove, className }: ExceptionsListProps) {
  const { t } = useTranslation('components')
  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <ul className="flex flex-col gap-1.5">
        {exceptions.map((e, i) => (
          <li
            key={`${e.date}-${i}`}
            className="flex items-center gap-3 rounded-xl border border-line bg-surface px-3 py-2"
          >
            <span className="w-16 flex-none font-mono text-[12px] font-semibold text-text-2">
              {e.date}
            </span>
            <span className="text-[12.5px] text-text">{e.label}</span>
            <span className="ml-auto flex items-center gap-2">
              <span className="rounded-pill bg-field px-2.5 py-0.5 text-[10.5px] font-bold text-text-2">
                {e.closed === false
                  ? t('schedule.exceptions.openTag')
                  : t('schedule.exceptions.closedTag')}
              </span>
              <button
                type="button"
                onClick={() => onRemove(i)}
                aria-label={`${t('schedule.exceptions.remove')}: ${e.label}`}
                className="grid size-5 place-items-center rounded-full text-text-3 transition-colors hover:bg-field hover:text-text focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue [&_svg]:size-3.5"
              >
                <XIcon />
              </button>
            </span>
          </li>
        ))}
      </ul>
      {onAdd && (
        <div>
          <button
            type="button"
            onClick={onAdd}
            className="rounded-lg border border-input-line bg-surface px-3 py-1.5 text-[12px] font-semibold text-text-2 transition-colors hover:border-brand-blue hover:text-link focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-blue"
          >
            {t('schedule.exceptions.add')}
          </button>
        </div>
      )}
    </div>
  )
}

export interface OutOfHoursMessageEditorProps {
  value: string
  onChange: (value: string) => void
  label?: string
  hint?: ReactNode
  className?: string
}

/** Textarea for the closed-hours auto-reply; the default hint reminds the
 *  operator to keep an emergency exit in the copy. */
export function OutOfHoursMessageEditor({
  value,
  onChange,
  label,
  hint,
  className,
}: OutOfHoursMessageEditorProps) {
  const { t } = useTranslation('components')
  return (
    <label className={cn('flex flex-col gap-1.5', className)}>
      <span className="text-[11px] font-semibold text-text">
        {label ?? t('schedule.outOfHours.label')}
      </span>
      <textarea
        value={value}
        onChange={(e) => onChange(e.target.value)}
        rows={3}
        className="resize-y rounded-xl border border-input-line bg-surface p-3 text-[12.5px] leading-relaxed text-text outline-none focus-visible:border-brand-blue"
      />
      <span className="text-[10.5px] text-text-3">{hint ?? t('schedule.outOfHours.hint')}</span>
    </label>
  )
}

export interface ProactiveWindowPickerProps {
  /** Selected day indices, Monday-first (0 = Monday). */
  daysOfWeek: number[]
  onChange: (days: number[]) => void
  className?: string
}

/** Day-of-week toggle buttons (Monday-first indices) bounding when the bot may
 *  START a conversation — always inside WhatsApp's free 24h window. Buttons
 *  show an initial but carry the full day name via aria-label/title. */
export function ProactiveWindowPicker({
  daysOfWeek,
  onChange,
  className,
}: ProactiveWindowPickerProps) {
  const { t } = useTranslation('components')
  function toggle(day: number) {
    onChange(
      daysOfWeek.includes(day)
        ? daysOfWeek.filter((d) => d !== day)
        : [...daysOfWeek, day].sort((a, b) => a - b),
    )
  }
  return (
    <div
      role="group"
      aria-label={t('schedule.proactive.label')}
      className={cn('flex gap-1.5', className)}
    >
      {DAY_KEYS.map((key, day) => {
        const on = daysOfWeek.includes(day)
        const name = t(`schedule.days.${key}.name`)
        return (
          <button
            key={key}
            type="button"
            aria-pressed={on}
            aria-label={name}
            title={name}
            onClick={() => toggle(day)}
            className={cn(
              'grid size-9 place-items-center rounded-lg border text-[12px] font-bold transition-colors',
              'focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-brand-blue',
              on
                ? 'border-brand-blue bg-brand-blue text-white'
                : 'border-input-line bg-surface text-text-2 hover:border-text-3',
            )}
          >
            {t(`schedule.days.${key}.initial`)}
          </button>
        )
      })}
    </div>
  )
}
